/**
 * 表示言語の切替。
 *
 * インドネシアの開発者が使うため英語を用意する。既定は日本語。
 *
 * ⛔**制度用語は翻訳しない。**
 * 評価基準の本文、台帳名・様式名、規程名、ロールコード、
 * 台帳に保存される選択肢の**値**（業務／開発ツール／退職 など）は
 * サーバから来たまま表示する。理由は2つ:
 *
 *  1. **証跡としての同一性が失われる。** 評価基準の本文は IPA の公式Excel
 *     そのままで、申請パッケージにもその文言で焼き込まれる。画面で英訳すると
 *     「専門家が見た文言」と「システムが出力した文言」が食い違う。
 *  2. **台帳の値を翻訳すると別の値になる。** 選択肢は列の値そのものなので、
 *     英語で保存すると as-of 出力が言語によって変わる。
 *
 * 翻訳するのは**操作のための文言**だけ（ボタン、見出し、説明、エラー）。
 * 申請フォームの項目名は例外的に訳す必要があるので、
 * フォーム定義側に `titleEn` を持たせ、**値は原語のまま送る**
 * （表示と保存を分ける）。
 *
 * ライブラリは入れない。キー数が少なく、複数形や語形変化の要件も無い。
 */
export type Lang = "ja" | "en";

type Dict = Record<string, string>;

const ja: Dict = {
  // ── 共通 ──
  "app.title": "SCS-Hub",
  "app.subtitle": "SCS制度★3 運用",
  "nav.dashboard": "準備度",
  "nav.registers": "台帳",
  "nav.requests": "申請",
  "nav.obligations": "期限",
  "common.loading": "読み込み中…",
  "common.back": "戻る",
  "common.none": "表示する項目はありません。",
  "common.logout": "ログアウト",
  "common.required": "必須",
  "common.forbidden": "この操作に必要なロールがありません（体制画面で割当を確認してください）",
  "common.loadFailed": "読み込めませんでした。ページを再読み込みしてください。続く場合はセキュリティ担当に連絡してください。",
  "common.forbiddenView": "この内容を見る権限がありません。必要な場合はセキュリティ担当に依頼してください。",
  "common.doneBy": "この操作は {roles} が行います。",
  "common.showDetail": "詳細を開く",
  "common.hideDetail": "詳細を閉じる",
  "common.select": "（選択してください）",
  "common.others": "他 {n} 件",
  "common.calculating": "集計中…",
  "viewpoint.pinned": "{time} 時点の記録を表示中",
  "viewpoint.reset": "最新に合わせる",
  "viewpoint.hint": "この時刻までに記録された内容だけを表示しています。閲覧中に他の人が追記しても、画面ごとに見える内容がずれません。",

  // ── ログイン ──
  "login.title": "SCS-Hub にログイン",
  "login.email": "メールアドレス",
  "login.password": "パスワード",
  "login.totp": "認証アプリのコード（6桁）",
  "login.submit": "ログイン",
  "login.submitting": "確認中…",
  "login.err.credentials": "メールアドレスかパスワードが違います。分からなくなったらセキュリティ担当部署長に連絡してください。",
  "login.err.totpRequired": "認証アプリのコードを入力してください",
  "login.err.totpInvalid": "コードが違います。認証アプリに今表示されている6桁を入れ直してください。",
  "login.err.network": "サーバーに接続できませんでした。入力は残っています。通信を確かめて、もう一度「ログイン」を押してください。",
  "login.totpPrompt": "認証アプリに表示されている6桁のコードを入力してください。",
  "login.expired": "時間が経ったのでログインし直してください。ログインすると元の画面に戻ります。",
  "review.heading": "これはレビュー用の環境です。本番ではありません。",
  "review.body": "インターネットに公開されているため、実際の機密情報・個人情報・認証情報を入力しないでください。入力された内容は業務要件の確認のためだけに使われ、実運用には引き継ぎません。",
  "review.deployment": "配備",

  // ── ダッシュボード ──
  "dash.title": "申請準備度",
  "dash.asOf": "基準日",
  "dash.asOfHint": "基準日を変えると、その日時点の姿で数え直します。申請に出すのはこの日付の状態です。",
  "dash.criteria": "★3 評価基準 {n} 項目",
  "dash.linked": "証跡が紐づいている",
  "dash.resolved": "実体まで確認できた",
  "dash.twoNumbers": "2つの数字が違うことに意味があります。",
  "dash.twoNumbersBody": "紐づけはあっても、基準日時点で台帳が空なら証跡になりません。下の数字が申請に使える件数です。",
  "dash.deadlines": "点検・報告の期限",
  "dash.overdue": "期限超過 {n} 件",
  "dash.dueSoon": "30日以内 {a} 件／未完了 {b} 件",
  "dash.blocked": "止まっている申請",
  "dash.blockedNone": "承認者未定で止まっている申請はありません。",
  "dash.blockedSome": "{n} 件が承認者未定で止まっています。",
  "dash.blockedDecide": "を決める",
  "dash.gaps": "証跡が足りていない項目",
  "dash.noEvidence": "証跡が紐づいていない（{n} 件）",
  "dash.noEvidenceHint": "台帳ではなく、規程文書・教育記録・設定エビデンスが証跡になる項目が多く含まれます。",
  "dash.emptyEvidence": "紐づけはあるが中身が空（{n} 件）",
  "dash.emptyEvidenceHint1": "台帳にデータを入れれば解決します。",
  "dash.emptyEvidenceHint2": "「証跡あり」と表示して中身が空",
  "dash.emptyEvidenceHint3": "という状態を防ぐため、別に数えています。",
  "dash.package": "申請パッケージ",
  "dash.packageHint1": "基準日の状態で、自己評価シート・証跡・台帳・申請の記録を1つの ZIP にまとめます。同じ基準日なら、",
  "dash.packageHint2": "何度作っても中身は同じ",
  "dash.packageHint3": "です。",
  "dash.packagePreview": "中身を確認する",
  "dash.packageFiles": "ファイル {n} 件 / {bytes} バイト",
  "dash.packageProblems": "提出までに解決が必要なこと: {n} 件",
  "dash.packageFrozen": "提出物として固定するのは最初に確定出力したファイルです。",

  // ── 期限 ──
  "obl.title": "点検・報告の期限",
  "obl.all": "すべて",
  "obl.due": "期限",
  "obl.status": "状態",
  "obl.severity": "重要度",
  "obl.content": "内容",
  "obl.submitTo": "提出先",
  "obl.criteria": "評価基準",
  "obl.complete": "完了",
  "obl.waive": "免除",
  "obl.waivePrompt": "免除の理由（該当なしの根拠が証跡になります）",
  "obl.waiveReasonLabel": "免除の理由（該当なしの根拠が証跡になります）",
  "obl.waiveSubmit": "免除する",
  "obl.waiveConfirm": "取り消せない記録として、「{title}」（期限 {date}）を免除しますか？",
  "obl.filterLabel": "状態で絞り込む",
  "obl.completeConfirm": "取り消せない記録として、「{title}」（期限 {date}）を完了にしますか？",
  "obl.sev.NORMAL": "通常",
  "obl.sev.HIGH": "高",
  "obl.sev.CRITICAL": "最重要",
  "obl.st.OVERDUE": "期限超過",
  "obl.st.SCHEDULED": "予定",
  "obl.st.OPEN": "進行中",
  "obl.st.COMPLETED": "完了",
  "obl.st.WAIVED": "免除",
  "obl.st.SUBMITTED": "提出済み",
  "dash.manifest": "内容の照合値（SHA-256）",

  // ── 申請 ──
  "req.title": "申請",
  "req.new": "新しい申請",
  "req.tab.pending": "自分の対応待ち（承認・実施）",
  "req.tab.mine": "自分が出した申請",
  "req.tab.all": "すべて",
  "req.no": "受付番号",
  "req.type": "種別",
  "req.state": "状態",
  "req.submitted": "提出日時",
  "req.steps": "承認ステップ",
  "req.empty": "該当する申請はありません。",
  "req.blockedHeading": "承認者が決まっていないため止まっている申請が {n} 件あります。",
  "req.blockedNeeds": "の担当者を決めれば進みます",
  "req.originRatio": "直近1年の台帳更新 {total} 件のうち、申請の承認由来は {n} 件（{pct}%）。",
  "req.originRatioRest": "残りは手動更新（理由の記録あり）。",
  "req.originRatioHint": "専門家確認では「台帳の更新が承認を経ているか」が問われます。ゼロにするより、計測できていることが重要です。",
  "req.kind": "申請の種類",
  "req.route": "承認経路",
  "req.conditional": "条件付き",
  "req.basis": "根拠",
  "req.effectiveDate": "発効日（任意）",
  "req.effectiveDateHint": "空欄なら、承認（実施記録が必要な申請は実施記録）をした日から台帳に反映されます。未来の日付にすると、承認しても台帳に現れるのはその日からです。異動や退職の予定を前もって申請できます。",
  "req.submit": "提出する",
  "req.submitting": "提出中…",
  "req.submitNote": "提出すると受付番号が採番され、以後この番号は変わりません。",
  "req.payload": "申請内容",
  "req.effect": "承認したときの台帳への影響",
  "req.effectNone": "台帳を更新しない系統です（申請と承認の記録だけが残ります）。",
  "req.col.field": "項目",
  "req.col.before": "現在",
  "req.col.after": "承認後",
  "req.separation": "職務分離の警告",
  "req.sepSame": "申請者と承認者が同一です。",
  "req.sepMulti": "同一人物が複数の承認ステップを担当しています。",
  "req.sepBody": "この申請は提出できます。ただし承認する人は、",
  "req.sepBodyStrong": "「分離できない理由」と「補完措置」の両方を記入しないと承認できません。",
  "req.sepReason": "分離できない理由",
  "req.sepReasonPh": "例: 部署に他に承認できる者がいない",
  "req.sepControl": "補完措置",
  "req.sepControlPh": "例: 四半期ごとに代表取締役が承認結果を点検する",
  "req.sepEvidence": "ここに書いた補完措置は、専門家確認で",
  "req.sepEvidenceStrong": "統制が効いている証拠",
  "req.sepEvidenceRest": "として提出されます。",
  "req.history": "経過",
  "req.decision": "決定（ステップ {n} / {role}）",
  "req.decisionHelp": "差し戻す: 申請者に修正を求めます（申請者が出し直します）。却下する: 認めずに終了します。どちらも取り消せません。",
  "req.waitingFor": "承認待ち: ステップ {n} / {role}（担当: {who}）",
  "req.comment": "コメント（差し戻し・却下のときは理由を必ず書いてください。申請者に表示されます）",
  "req.approve": "承認する",
  "req.return": "差し戻す",
  "req.reject": "却下する",
  "req.applied": "実施記録",
  "req.appliedHint": "この系統は承認だけでは台帳に反映されません。実際の作業を終え、作業報告書を受領してから登録してください（4-1-1-1 の実施記録）。",
  "req.appliedField": "実施内容・受領した報告書",
  "req.appliedSubmit": "実施を記録して台帳へ反映",
  "req.waitingApply": "承認済みです。担当者が作業を終えて実施を記録すると、台帳に反映されます。",
  "req.withdraw": "取り下げる",
  "req.withdrawNote": "取り下げても記録は残ります（削除はできません）。",
  "req.withdrawReason": "取り下げの理由（任意）",
  "req.returnedHelp": "この申請は差し戻されました。下の履歴にあるコメントを確認し、内容を直して「新しい申請」から出し直してください。この申請は「取り下げる」で閉じてください（記録は残ります）。",
  "req.unresolvedRole": "承認者の担当者が決まっていないため、この申請は進みません。{roles} の実名を割り当ててください。",
  "req.applicant": "申請者",
  "req.col.step": "ステップ",
  "req.col.role": "役割",
  "req.col.decidedBy": "決定した人",
  "req.col.decidedAt": "決定日時",
  "req.col.comment": "コメント",
  "req.notFound": "この申請は見つからないか、表示する権限がありません。",
  "req.confirmApproveFinal": "承認すると、上の「承認したときの台帳への影響」のとおり台帳に反映されます（実施記録が必要な系統は、実施担当が記録した後に反映されます）。承認は取り消せません。承認しますか？",
  "req.confirmApproveStep": "承認すると次のステップ（{role}）に回ります。取り消せません。承認しますか？",
  "req.confirmReturn": "申請者に差し戻します。申請者は内容を直して出し直す必要があります。差し戻しますか？",
  "req.confirmReject": "却下すると、この申請は終了し、再開できません。却下しますか？",
  "req.confirmWithdraw": "この申請を取り下げます。取り下げた申請は再開できません（記録は残ります）。取り下げますか？",
  "req.confirmApply": "実施を記録すると台帳に反映され、取り消せません。記録しますか？",
  "req.ev.SUBMIT": "提出",
  "req.ev.APPROVE": "承認",
  "req.ev.REJECT": "却下",
  "req.ev.RETURN": "差し戻し",
  "req.ev.WITHDRAW": "取り下げ",
  "req.ev.APPLY": "実施を記録",
  "step.APPROVED": "承認",
  "step.REJECTED": "却下",
  "step.RETURNED": "差し戻し",
  "step.PENDING": "承認待ち",
  "step.unassigned": "担当者未定",
  "req.empty.pending": "あなたの承認・実施待ちの申請はありません。",
  "req.empty.mine": "あなたが出した申請はまだありません。「新しい申請」から作成できます。",
  "req.err.ack": "申請者と承認者が同一です。理由と補完措置の両方を入力してください",
  "req.err.stale": "提出後に対象の台帳が別経路で更新されています。内容を確認して申請し直してください",
  "req.err.forbidden": "この申請を承認する権限がありません",
  "req.err.badState": "この申請は既に決定済みです",
  "req.err.generic": "処理できませんでした",
  "req.err.payload": "入力内容を確認してください",
  "req.err.noRoute": "この系統の承認経路が設定されていません。管理者に連絡してください",
  "req.err.separation": "申請者と承認者が同一のため、この系統では提出できません",
  "req.err.submit": "提出できませんでした",

  // 申請の状態
  "state.DRAFT": "下書き",
  "state.SUBMITTED": "提出済",
  "state.IN_REVIEW": "審査中",
  "state.APPROVED": "承認済（未反映）",
  "state.APPLIED": "反映済",
  "state.RETURNED": "差戻し",
  "state.WITHDRAWN": "取下げ",
  "state.REJECTED": "却下",
  "state.EXPIRED": "失効",


  // ── インシデント ──
  "nav.incidents": "インシデント",
  "inc.title": "セキュリティインシデント",
  "inc.report": "発見を報告する",
  "inc.reportHint": "まず電話で連絡してください。連絡がついたら、ここに記録を残してください（08番手順書）。受付番号と受付日時は自動で付き、後から変更できません。",
  "inc.callWho": "連絡先: {roleName} {name}（代理: {deputy}）",
  "inc.callWhoVacant": "インシデント一次受付の担当者が決まっていません。セキュリティ担当部署に連絡してください。",
  "inc.receivedDone": "受け付けました。受付番号: {no}（{time}）。問い合わせるときはこの番号を伝えてください。",
  "inc.listRestricted": "インシデントの一覧を見られるのは担当者だけです。あなたの報告は、送信したときに表示された受付番号で受け付けられています。",
  "inc.detectedAt": "発見日時",
  "inc.detectedAtLocalHint": "あなたのPCの時刻",
  "inc.summary": "何が起きたか",
  "inc.summaryPh": "例: 業務PCで身に覚えのないログイン通知を受け取った",
  "inc.route": "検知経路",
  "inc.routePh": "例: UTMのアラート / 本人の気付き / 委託先からの連絡",
  "inc.assets": "影響を受けた資産",
  "inc.personal": "個人データが関わる可能性がある",
  "inc.personalHint": "迷ったら「はい」。外部報告の要否判断の入口になります。",
  "inc.vendorOrg": "委託先からの報告の場合、その組織",
  "inc.submit": "報告する",
  "inc.no": "受付番号",
  "inc.stage": "段階",
  "inc.detected": "発見",
  "inc.received": "受付",
  "inc.advance": "段階を進める",
  "inc.advanceHint": "段階は戻せません。訂正が必要なときはセキュリティ担当に連絡してください。",
  "inc.initialAction": "初動の内容",
  "inc.cause": "原因",
  "inc.preventive": "再発防止策",
  "inc.extReport": "外部報告の判断",
  "inc.extReportHint": "報告しないと判断した場合も、その判断を記録してください。記録が無いと、判断したのか見落としたのかを翌年の点検で区別できません。",
  "inc.decisionLabel": "外部への報告",
  "inc.saveDecision": "判断を記録する",
  "inc.err.reason": "判断の理由を入力してください。報告しない場合も理由が必要です。",
  "inc.advanceConfirm": "段階を「{next}」に進めます。段階は戻せません。よろしいですか？",
  "inc.reportedConfirm": "{label} を報告済みとして記録します。取り消せません。よろしいですか？",
  "inc.dest": "報告先",
  "inc.required": "報告する",
  "inc.notRequired": "報告しない",
  "inc.reason": "判断の理由",
  "inc.phase": "段階",
  "inc.phasePrelim": "速報",
  "inc.phaseDefinitive": "確報",
  "inc.malicious": "不正の目的のおそれがある",
  "inc.due": "期限",
  "inc.noDue": "法定の日数なし（速やかに）",
  "inc.markReported": "報告済みにする",
  "inc.judgeConfirm": "外部報告の判断（{required}）を記録します。よろしいですか？",
  "inc.pendingReports": "未実施の外部報告",
  "inc.overdue": "期限超過",
  "inc.empty": "記録されたインシデントはありません。",

  // ── 教育 ──
  "nav.training": "教育",
  "trn.title": "セキュリティ教育",
  "trn.sessions": "開催一覧",
  "trn.open": "新しい開催",
  "trn.openConfirm": "この教材の開催を登録します。対象者の割当は開催後に固定されます。よろしいですか？",
  "trn.course": "教材",
  "trn.sessionTitle": "開催の名称",
  "trn.method": "実施方法",
  "trn.elearning": "eラーニング",
  "trn.classroom": "集合研修",
  "trn.scheduledOn": "実施日",
  "trn.targetNote": "対象者の範囲（メモ）",
  "trn.assigned": "割当",
  "trn.completed": "受講",
  "trn.exempt": "免除",
  "trn.rate": "受講率",
  "trn.notMeasurable": "測定不能",
  "trn.take": "受講を記録する",
  "trn.score": "確認テストの点数（0〜100）",
  "trn.scoreHint": "合格点に届かない場合、受講済みにはならず督促の対象に残ります。",
  "trn.passed": "合格しました",
  "trn.failed": "合格点に届いていません。もう一度受講してください。",
  "trn.recorded": "受講を記録しました。",
  "trn.pending": "未受講者",
  "trn.remind": "督促の対象を見る",
  "trn.empty": "開催はまだありません。",
  "trn.err.notAssigned": "この開催の対象者に入っていないため、受講を記録できません。対象のはずの場合は教育担当に連絡してください。",
  "trn.err.noTarget": "教育の対象者が1人もいないため、開催できません。利用者の「教育対象」の設定をシステム管理者に確認してください。",
  "trn.passMark": "合格点: {n}点",
  "trn.material": "教材を開く",
  "trn.my.assigned": "あなたは対象です（未受講）",
  "trn.my.completed": "受講済みです",
  "trn.my.notAssigned": "あなたはこの開催の対象ではありません",
  "trn.closed": "この開催は締め切られました",
  "trn.assignedNote": "割当は開催時点の対象者で固定されます。後から入社した人は分母に入りません。",


  // ── 評価基準と証跡 ──
  "nav.criteria": "評価基準",
  "cri.title": "評価基準と証跡",
  "cri.progress": "★3 {total} 項目のうち、証跡が紐づいているのは {linked} 件、実体まで確認できたのは {resolved} 件。",
  "cri.filter.gap": "証跡が無い",
  "cri.filter.empty": "紐づけはあるが空",
  "cri.filter.all": "すべて",
  "cri.noneLeft": "この条件に該当する項目はありません。",
  "cri.code": "評価基準",
  "cri.deliverable": "求められる成果物",
  "cri.evidence": "証跡",
  "cri.noLink": "未紐づけ",
  "cri.relatedRegisters": "関係する台帳",
  "cri.assess": "自己評価",
  "cri.conclusion": "結論",
  "cri.c.MET": "適合",
  "cri.c.PARTIAL": "一部適合",
  "cri.c.NOT_MET": "不適合",
  "cri.c.NOT_APPLICABLE": "該当なし",
  "cri.rationale": "根拠（なぜそう言えるか）",
  "cri.rationalePh": "例: 様式3の台帳で全端末のOSと製造元を管理し、四半期ごとに棚卸している",
  "cri.rationaleHint": "証跡だけでは「なぜ適合か」を示せません。根拠の文章がそのまま自己評価シートに載ります。",
  "cri.naJust": "「該当なし」の根拠",
  "cri.naJustPh": "例: 共有IDを一切発行していない（様式11は該当なし）",
  "cri.saveAssessment": "自己評価を保存",
  "cri.addEvidence": "証跡を足す",
  "cri.addEvidenceHint": "規程文書・教育記録・設定エビデンスの置き場所をURLで指せます。台帳が証跡になる項目は、台帳にデータを入れれば自動で解決します。",
  "cri.evidenceTitle": "証跡の名称",
  "cri.evidenceTitlePh": "例: 情報セキュリティ基本方針（社内ポータル）",
  "cri.evidenceUrl": "URL",
  "cri.linkEvidence": "この評価基準に紐づける",
  "cri.done": "保存しました。",
  "cri.noCycle": "自己評価サイクルがまだありません。先にサイクルを作成してください。",
  "cri.cycleOrder": "SCS-100 3.1.1 の順序: ①自己評価 → ②★3確認責任者の確認・署名 → ③自己適合宣言 → ④IPAへ申請 → ⑤登録。順序は飛ばせません。",
  "cri.ready": "宣言できる状態です。",
  "cri.notReady": "宣言できない理由が {n} 件あります。",
  "cri.startConfirm": "★3確認責任者の確認に回す",
  "cri.startConfirmConfirm": "{label} を★3確認責任者の確認に回します。よろしいですか？",
  "cri.confirmerName": "★3確認責任者の氏名",
  "cri.confirmerQual": "資格（6資格のいずれか＋研修受講）",
  "cri.confirmerRegNo": "登録番号（任意）",
  "cri.confirmerAffiliation": "所属",
  "cri.affiliation.INTERNAL": "社内",
  "cri.affiliation.EXTERNAL": "社外",
  "cri.sign": "確認責任者として署名する",
  "cri.signConfirm": "★3確認責任者として、{label} の自己評価を確認したことを署名として記録します。署名は取り消せません。署名しますか？",
  "cri.signedMsg": "署名しました。",
  "cri.declare": "自己適合宣言を行う（経営層）",
  "cri.declareConfirm": "{label} について自己適合宣言を行います。経営層の宣言として記録され、取り消せません。宣言しますか？",
  "cri.markSubmitted": "IPAへ申請済みにする",
  "cri.submittedConfirm": "IPA への申請を実際に済ませた後に押してください。申請済みとして記録され、取り消せません。記録しますか？",
  "cri.statusChanged": "「{status}」になりました。",
  "cri.markRegistered": "登録完了を記録する",
  "cri.registeredAt": "登録完了通知日（IPA からの通知に書かれた日付）",
  "cri.registeredHint": "この日付から翌年の更新期限が決まります。",
  "cri.registeredConfirm": "登録完了通知日を {date} として記録します。取り消せません。よろしいですか？",
  "cri.current": "保存済みの自己評価: {conclusion}（{date} {who}）",
  "cri.notAssessed": "まだ自己評価していません",
  "cri.err.conclusion": "結論を選んでください。",
  "cri.err.rationale": "根拠を入力してください。",
  "cri.addComment": "確認責任者としての指摘を記録",
  "cri.commentPh": "例: 根拠に棚卸の頻度が書かれていない",
  "cri.confirmerCommentLabel": "確認責任者の指摘",
  "cycle.st.OPEN": "自己評価の記入中",
  "cycle.st.CONFIRMING": "★3確認責任者の確認中",
  "cycle.st.DECLARED": "自己適合宣言済み",
  "cycle.st.SUBMITTED": "IPAへ申請済み",
  "cycle.st.REGISTERED": "登録完了",


  // ── 設定 ──
  "nav.settings": "設定",
  // ── 台帳（操作部分のみ。台帳名・様式番号・列名・列の確度・値は制度用語なので訳さない） ──
  "reg.ledgers": "台帳（{n}）",
  "reg.records": "記録（{n}）",
  "reg.noForm": "様式なし",
  "reg.imported": "取込",
  "reg.importedHint": "外部から取り込む対象",
  "reg.restricted": "閲覧できるのは担当のロールの人だけです",
  "reg.restrictedLabel": "閲覧制限あり",
  "reg.notApplicable": "該当なし: {reason}",
  "reg.listError": "台帳の一覧を取得できませんでした",
  "reg.schemaError": "台帳の定義を取得できませんでした",
  "reg.loadError": "台帳を取得できませんでした",
  "reg.backToList": "← 台帳の一覧",
  "reg.criteria": "対応する評価基準:",
  "reg.asOf": "この日時点の状態を表示",
  "reg.includeDeleted": "廃止した行も表示",
  "reg.reload": "再読込",
  "reg.addRow": "行を追加",
  "reg.csv": "CSV（この時点）",
  "reg.importCsv": "CSV取込",
  "reg.importing": "取込中…",
  "reg.importDone": "取込: 新規 {c} / 更新 {u} / 変化なし {s} / エラー {e} / 取込元に無い行 {o}",
  "reg.onlyInSystem": "取込元に無い（消していません。廃棄なら DELETE の版を追記）",
  "reg.importFailed": "取込できませんでした",
  "reg.importConfirm": "{file} を取り込みます。観測日は {date} として記録されます。取り込んだ版は取り消せません。よろしいですか？",
  "reg.importPastDate": "「この日時点の状態を表示」の日付が今日ではありません（{date}）。過去の日付で取り込む場合だけ続けてください。",
  "reg.importHint": "「CSV（この時点）」で出力したファイルと同じ列にしてください。内容が変わらない行は版を作りません。",
  "reg.forbidden": "この台帳を見る権限がありません。必要な場合はセキュリティ担当に依頼してください。",
  "reg.count": "{n}件。",
  "reg.asOfHint1": "過去の日付を指定すると、その時点の姿がそのまま出ます。",
  "reg.col.key": "キー",
  "reg.col.status": "状態",
  "reg.col.validFrom": "発効日",
  "reg.col.history": "履歴",
  "reg.generation": "第{n}世代",
  "reg.retired": "廃止",
  "reg.active": "有効",
  "reg.history": "履歴",
  "reg.emptyAsOf": "この時点では1件もありません",
  "reg.yes": "はい",
  "reg.no": "いいえ",
  "reg.add.title": "行を追加",
  "reg.add.hint": "新しい行を登録します。登録した内容は上書きされず、修正は新しい版として残ります。",
  "reg.add.key": "キー（資産番号など）",
  "reg.add.keyExists": "このキーは既にあります。直す場合は一覧の「この行を直す」を使ってください（ここで登録すると、入力しなかった列は空になります）。",
  "reg.add.multiHint": "複数あるときは「、」で区切ってください",
  "reg.add.errJson": "{label} が正しい JSON の形式ではありません",
  "reg.add.derivedUnset": "（未設定）",
  "reg.add.boolUnset": "（未回答）",
  "reg.add.validFrom": "発効日",
  "reg.add.note": "変更理由",
  "reg.add.noteHint": "（後から「なぜこうなったか」を説明するために必須）",
  "reg.add.saving": "保存中…",
  "reg.add.submit": "登録する",
  "reg.add.cancel": "キャンセル",
  "reg.add.errSchema": "入力が台帳の定義に合いません",
  "reg.add.errBody": "必須項目が足りません（変更理由は必須です）",
  "reg.add.errSave": "保存できませんでした",
  "reg.edit": "この行を直す",
  "reg.editTitle": "{key} を直す",
  "reg.editHint": "現在の値を入れた状態でフォームを開いています。キーは変更できません。入力した内容がそのまま新しい版になります（他の列も含めてすべて保存し直されます）。",
  "reg.retire": "廃止する",
  "reg.retireTitle": "{key} を廃止する",
  "reg.retireConfirm": "{key} を {date} 付けで廃止します。履歴は残り、取り消しはできません。よろしいですか？",
  "reg.schemaErr.required": "{label} は必須です",
  "reg.schemaErr.enum": "{label} は選択肢から選んでください",
  "reg.schemaErr.format": "{label} の形式が正しくありません",
  "reg.hist.title": "{key} の履歴（{n}版）",
  "reg.hist.hint": "版は追記のみで、書き換えも削除もできません（DBの制約として強制しています）。",
  "reg.hist.seq": "版",
  "reg.hist.op": "操作",
  "reg.hist.recordedAt": "記録日時",
  "reg.hist.source": "由来",
  "reg.hist.note": "変更理由",
  "reg.hist.data": "内容",
  "reg.hist.close": "閉じる",
  "reg.op.CREATE": "登録",
  "reg.op.UPDATE": "変更",
  "reg.op.DELETE": "廃止",
  // ── ロール ──
  "nav.roles": "体制",
  "roles.title": "体制（ロールと実名）",
  "roles.hint": "担当者が決まっていない役割があると、その承認で申請が止まり、★3 の準備も進みません。休暇や退職に備えて、代理者も決めてください。",
  "roles.whoAssigns": "担当者を割り当てられるのは、セキュリティ統括責任者とセキュリティ担当部署長です。",
  "roles.vacantCount": "未定 {n} 件",
  "roles.noDeputyCount": "代理者なし {n} 件",
  "roles.loadError": "体制を取得できませんでした",
  "roles.col.role": "役割",
  "roles.col.primary": "主担当",
  "roles.col.deputy": "代理者",
  "roles.col.fallback": "未定時の暫定承認者",
  "roles.col.kind": "種別",
  "roles.col.holder": "担当",
  "roles.scoped": "システム・情報ごとに担当",
  "roles.vacant": "未定",
  "roles.noDeputyWarn": "代理者を置いてください",
  "roles.assignPrimary": "主担当を決める",
  "roles.assignDeputy": "代理者を決める",
  "roles.history": "履歴",
  "roles.historyTitle": "{code} の割当履歴",
  "roles.historyHint": "割当は追記のみ。交代は新しい行の追記で表し、過去の承認者が誰だったかは変わりません。",
  "roles.form.titlePrimary": "{role} の主担当",
  "roles.form.titleDeputy": "{role} の代理者",
  "roles.form.user": "担当者",
  "roles.form.choose": "（担当者を選んでください）",
  "roles.form.vacate": "この役割を空席にする",
  "roles.vacateConfirm": "{role} の{kind}を空席にします。承認者が空席になると、この役割を待つ申請は止まります。よろしいですか？",
  "roles.form.validFrom": "発効日",
  "roles.form.note": "根拠",
  "roles.form.notePh": "例: 2026-09-15 付 辞令 / 経営会議決裁",
  "roles.form.submit": "割り当てる",
  "roles.saveError": "保存できませんでした",
  "dash.roles": "体制",
  "dash.rolesVacant": "実名が未定のロール {n} 件",
  "dash.rolesOk": "17ロールすべてに実名が入っています。",
  "dash.rolesNoDeputy": "代理者なし {n} 件",
  "dash.rolesOpen": "体制を決める",
  "cri.addFile": "ファイル証跡を足す",
  "cri.addFileHint": "設定画面のスクリーンショットなど。保存時のハッシュと版で固定されるので、後で差し替えても過去の出力は変わりません。他人の氏名・メールが写り込んでいないか確認してから登録してください。",
  "cri.file": "ファイル",
  "cri.capturedAt": "取得日",
  "cri.targetSystem": "対象システム",
  "cri.targetSystemPh": "例: Google Workspace 管理コンソール",
  "cri.uploading": "登録中…",
  "cri.uploadAndLink": "登録して紐づける",
  "dash.finalize": "確定出力",
  "dash.finalizeHint": "作った ZIP を、消せない場所に保管します。「事務局提出用」は5年間、管理者も含めて誰も消せません。",
  "dash.finalizeRun": "確定出力する",
  "dash.finalizeConfirm": "基準日 {asOf} の状態を「事務局提出用」として確定します。確定したファイルは5年間、管理者を含めて誰も削除・変更できません。よろしいですか？",
  "dash.finalizeDone": "確定しました（照合値 {hash}…）",
  "dash.finalizeFailed": "確定できませんでした",
  "dash.purpose.DRAFT": "下書き",
  "dash.purpose.CONFIRMER_REVIEW": "確認責任者レビュー用",
  "dash.purpose.SUBMISSION": "事務局提出用",
  "dash.ex.when": "出力日時",
  "dash.ex.purpose": "用途",
  "dash.ex.purposeLabel": "用途",
  "dash.ex.status": "状態",
  "dash.ex.lock": "保持期限",
  "dash.ex.by": "出力者",
  "dash.exst.RUNNING": "出力中",
  "dash.exst.READY": "検証待ち",
  "dash.exst.VERIFIED": "確定済み",
  "dash.exst.FAILED": "失敗",
  "dash.oblOpen": "期限の一覧を開く",
  // ── 運用管理 ──
  "nav.governance": "運用管理",
  "gov.title": "運用管理",
  "gov.tab.rules": "数値ルール",
  "gov.tab.documents": "規程文書",
  "gov.tab.corrective": "是正措置",
  "gov.tab.audit": "監査ログ",
  "gov.rules.hint": "制度で決まっている値は変えられません。制度が限度を決めている値（年1回以上など）は、厳しくする方向にだけ変えられます。当社が決める値は変えられ、変更の履歴が残ります。",
  "gov.rules.name": "ルール",
  "gov.rules.value": "値",
  "gov.rules.range": "範囲",
  "gov.rules.source": "根拠",
  "gov.rules.change": "変更",
  "gov.rules.note": "変更理由",
  "gov.rules.save": "新しい版を追記",
  "gov.rules.err.number": "数値で入力してください（単位: {unit}）。",
  "gov.rules.err.format": "値の形式が正しくありません。項目ごとに入力し直してください。",
  "gov.docs.hint": "11文書の制定日・改訂履歴・承認記録。版は追記のみで、過去の版は書き換えません。",
  "gov.docs.title": "文書",
  "gov.docs.kind": "種別",
  "gov.docs.current": "現行版",
  "gov.docs.established": "制定・改訂日",
  "gov.docs.approved": "承認",
  "gov.docs.none": "版なし",
  "gov.docs.addVersion": "版を追加",
  "gov.docs.addVersionTitle": "{docNo} 番に版を追加",
  "gov.docs.version": "版",
  "gov.docs.revisedAt": "改訂日",
  "gov.docs.approvedBy": "承認ロール",
  "gov.docs.approvedAt": "承認日",
  "gov.docs.file": "ファイル（任意）",
  "gov.docs.changeNote": "改訂内容",
  "gov.docs.save": "追記する",
  "gov.ca.hint": "点検・専門家確認で見つかった指摘の是正。未是正のまま翌年に持ち越す場合は「持ち越し」で新しい行に繋ぎます（黙って消えません）。",
  "gov.ca.openCount": "未是正 {n} 件",
  "gov.ca.add": "指摘を起票",
  "gov.ca.editDue": "期限・メモを変える",
  "gov.ca.dueTitle": "是正措置の期限を変える",
  "gov.ca.dueCurrent": "いまの期限: {date}",
  "gov.ca.dueNew": "新しい期限",
  "gov.ca.dueReason": "理由（任意）",
  "gov.ca.dueReasonReq": "理由（期限を後ろへずらすときは必須）",
  "gov.ca.dueLaterNote": "期限を後ろへずらします。理由は是正措置のメモと監査ログに残ります。",
  "gov.ca.dueMemo": "メモ（任意。是正措置のメモに足されます）",
  "gov.ca.dueNoChange": "期限かメモを変えてください",
  "gov.ca.dueReasonNeeded": "期限を後ろへずらすには、理由を入力してください",
  "gov.ca.dueSaved": "期限を変えました",
  "gov.ca.memoSaved": "メモを足しました",
  "gov.ca.dueSave": "変更する",
  "gov.ca.status": "状態",
  "gov.ca.titleCol": "件名",
  "gov.ca.finding": "指摘内容",
  "gov.ca.owner": "担当ロール",
  "gov.ca.due": "期限",
  "gov.ca.criteria": "評価基準",
  "gov.ca.notePh": "何をしたか",
  "gov.ca.start": "着手",
  "gov.ca.done": "完了",
  "gov.ca.carryOver": "持ち越し",
  "gov.ca.doneConfirm": "この是正措置を完了にします。完了からは戻せません。よろしいですか？",
  "gov.ca.carryOverConfirm": "この是正措置を次のサイクルに持ち越します。持ち越しからは戻せません。よろしいですか？",
  "gov.ca.carried": "持ち越し分",
  "gov.ca.save": "起票する",
  "gov.ca.s.OPEN": "未着手",
  "gov.ca.s.IN_PROGRESS": "対応中",
  "gov.ca.s.DONE": "完了",
  "gov.ca.s.CARRIED_OVER": "持ち越し済",
  "gov.audit.hint": "誰がいつ何をしたかの記録です。後から書き換えられないように保管しています。この画面を開いたことも記録されます。",
  "gov.audit.filterPh": "操作で絞る（例: auth. / register. / access.denied）",
  "gov.audit.verify": "チェーンを検証",
  "gov.audit.chainOk": "チェーン正常（{n} 行）",
  "gov.audit.chainBroken": "記録のつながりが途切れています（id {id}）。改ざんか障害の可能性があります。すぐにセキュリティ統括責任者に連絡してください。",
  "gov.audit.outcome.SUCCESS": "成功",
  "gov.audit.outcome.DENIED": "拒否",
  "gov.audit.outcome.ERROR": "エラー",
  "gov.audit.anchors": "S3 アンカー {n} 日分",
  "gov.audit.when": "日時",
  "gov.audit.actor": "実行者",
  "gov.audit.action": "操作",
  "gov.audit.target": "対象",
  "gov.audit.outcome": "結果",
  "gov.tab.vendors": "委託先照会",
  "gov.tab.users": "利用者",
  "gov.vd.hint": "委託先への照会38件。評価基準ごとに、どの委託先の回答を待っているかが分かります。受け取った回答（受領日・対象期間・ファイル）は「委託先証跡の受領記録」に、照会の No. を付けて登録してください。",
  "gov.vd.evidenceLinkLabel": "委託先証跡の受領記録",
  "gov.vd.summary": "回答待ち {pending} / {total}",
  "gov.vd.all": "すべての委託先",
  "gov.vd.vendor": "委託先",
  "gov.vd.question": "照会内容",
  "gov.vd.criteria": "評価基準",
  "gov.vd.status": "状態",
  "gov.vd.sent": "送付",
  "gov.vd.answered": "回答",
  "gov.vd.received": "受領証跡 {n} 件",
  "gov.vd.markSent": "送付した",
  "gov.vd.markAnswered": "回答を受領した",
  "gov.vd.notNeeded": "不要",
  "gov.vd.notNeededConfirm": "この照会を「不要」にします。元に戻す手段はありません。よろしいですか？",
  "gov.vd.whyNotNeeded": "照会しない理由（証跡になります）",
  "gov.vd.evidenceHint": "受領した証跡の登録先:",
  "gov.us.hint": "退職を記録すると、その人が持つ ID ごとに削除申請が自動で起票され、ロールは空席になり、セッションは切れます。利用者は削除しません（過去の記録の主体として残ります）。消去請求には仮名化で応じます。",
  "gov.us.unused": "未使用ID {n} 件（{days}日以上ログインなし）",
  "gov.us.days": "日",
  "gov.us.name": "利用者",
  "gov.us.status": "状態",
  "gov.us.lastLogin": "最終ログイン",
  "gov.us.s.ACTIVE": "在籍",
  "gov.us.s.SUSPENDED": "停止中",
  "gov.us.s.RETIRED": "退職",
  "gov.us.suspend": "停止",
  "gov.us.retire": "退職を記録",
  "gov.us.reactivate": "復帰",
  "gov.us.pseudonymize": "仮名化",
  "gov.us.pseudonymized": "仮名化済",
  "gov.us.notePrompt": "{name} を「{status}」にする根拠（辞令・届出など）",
  "gov.us.datePrompt": "発効日（YYYY-MM-DD）",
  "gov.us.retired": "退職を記録しました。削除申請 {n} 件を起票し、ロール {roles} 件を空席にしました。",
  "gov.us.pseudoPrompt": "{name} を仮名化する理由（消去請求の受付日など）。元に戻せません。",
  "gov.us.effectiveDate": "発効日",
  "gov.us.note": "根拠（辞令・届出など）",
  "gov.us.retireConfirm": "{name} の退職を {date} 付けで記録します。持っているIDの削除申請を自動で作成し、担当している役割を空席にし、ログイン中のセッションを切ります。元に戻せません。よろしいですか？",
  "gov.us.statusConfirm": "{name} を「{status}」に {date} 付けで変更します。よろしいですか？",
  "gov.us.pseudoReason": "仮名化する理由（消去請求の受付日など）",
  "gov.us.pseudoConfirm": "{name} を仮名化します。元に戻せません。よろしいですか？",
  "cri.vendorWaiting": "委託先照会",
  "inc.share": "事例として共有する",
  "inc.shareHint": "再発防止策つきで教材にし、教育と一体で記録します（機能22）。",
  "inc.sharePrompt": "教材に載せる説明（個人情報・顧客名を落としてください）",
  "inc.shared": "教材 {code} を作りました。教育画面から開催できます。",
  "inc.alreadyShared": "既に教材になっています。",
  "obl.responses": "部署別回答",
  "obl.responsesTitle": "部署別回答",
  "obl.responsesHint": "設問ごとに回答者が決まっています。部署の設問は各部署が、会社全体に関わる設問はセキュリティ担当部署が回答します。どの回答にも、確認したこと・証跡が必要です（年次点検チェックシート）。",
  "obl.collect": "各部署に回答を依頼",
  "obl.collected": "{n} 件の回答を依頼し、{notified} 人に通知しました。",
  "obl.answeredCount": "回答 {a} / {b} 件",
  "obl.dept": "回答者",
  "obl.secTeam": "セキュリティ担当部署",
  "obl.secTeamNote": "会社全体の設問",
  "obl.round": "何回目の依頼か",
  "obl.respondedAt": "回答日時",
  "obl.answer": "回答",
  "obl.pending": "未回答",
  "obl.answerBtn": "回答する",
  "obl.q": "設問 {n}",
  "obl.as.DONE": "実施済",
  "obl.as.PARTIAL": "一部実施",
  "obl.as.NOT_DONE": "未実施",
  "obl.as.NA": "該当なし",
  "obl.basis": "確認したこと・証跡（資料名・日付・リンクなど）",
  "obl.basisHint": "実施済・一部実施・未実施・該当なしのどれでも必要です。一部実施・未実施は、できていない点も書いてください",
  "obl.criteriaBasis": "根拠の評価基準",
  "obl.noQuestions": "この依頼には、あなたが答える設問がありません。補足だけを書いて送れます。",
  "obl.ansSummary": "実施済 {done}・一部実施 {partial}・未実施 {notDone}・該当なし {na}",
  "obl.ansCorrective": "一部実施・未実施は是正措置に起票されます。",
  "obl.ansCorrectiveLink": "是正措置を見る",
  "obl.answerSentCa": "回答を送りました。是正措置を {n} 件起票しました。",
  "obl.err.pick": "設問 {n}: 実施済・一部実施・未実施・該当なしのどれかを選んでください",
  "obl.err.basis": "設問 {n}: 確認したこと・証跡を入力してください",
  "obl.err.basisLong": "設問 {n}: 根拠は{max}文字以内にしてください",
  "obl.err.stale": "設問 {n}: 設問が変わったようです。画面を読み込み直してください。{msg}",
  "obl.answerNote": "補足（任意）",
  "obl.collectDue": "回答期限",
  "obl.submitAnswer": "回答を送る",
  "obl.formTitle": "{who}の回答",
  "obl.progress": "{total}問中{done}問 回答済み",
  "obl.autosaveHint": "入力は自動で保存され、閉じても読み込み直しても残ります。送るまでは下書きです。",
  "obl.draftRestored": "入力の途中だった回答を戻しました。確かめて「回答を確認する」を押してください。",
  "obl.draftDiscard": "下書きを捨てて最初から答える",
  "obl.draftDiscardConfirm": "入力した内容を捨てて、最初から答え直しますか？",
  "obl.review": "回答を確認する",
  "obl.reviewTitle": "{who}の回答: 送る前の確認",
  "obl.reviewCorrective": "一部実施・未実施の {n} 問は是正措置として登録されます（期限 {date}）。",
  "obl.reviewIrreversible": "送ると修正できません。内容を確かめてから送ってください。",
  "obl.reviewSend": "この内容で送る",
  "obl.reviewBack": "戻って直す",
  "obl.hintTitle": "アプリ内の記録",
  "obl.hintAsOf": "（{date} 時点）",
  "obl.hintPrefix": "【アプリ内の記録 {date}】",
  "obl.hintInsert": "確認したことに入れる",
  "obl.hintInserted": "入れました",
  "obl.hintOpen": "記録を開く",
  "obl.hintNote": "アプリが持っている記録を見せているだけです。答え（実施済など）は、内容を確かめてご自身で選んでください。",
  "obl.evTitle": "証跡のリンク（任意）",
  "obl.evCount": "{n}/{max} 件",
  "obl.evUrl": "資料の置き場所（URL）",
  "obl.evHint": "http:// または https:// で始まるものだけ。入力したら「リンクを追加」を押してください",
  "obl.evName": "表示名（任意）",
  "obl.evAdd": "リンクを追加",
  "obl.evRemove": "リンク「{name}」を外す",
  "obl.evRemoveShort": "外す",
  "obl.evLinks": "証跡のリンク",
  "obl.err.linkUrl": "設問 {n}: リンクは http:// または https:// で始まる URL にしてください（表示名は200文字まで）",
  "obl.err.linkMany": "設問 {n}: リンクは {max} 件までです",
  "obl.err.linkPending": "設問 {n}: 入力したリンクが追加されていません。「リンクを追加」を押すか、消してください",
  "obl.prevOffer": "前回（{date}・{who}）の回答があります。{n} 問分を下書きに入れて、今回の状況に合わせて直せます。",
  "obl.prevUse": "前回の回答を下書きに入れる",
  "obl.prevCopied": "前回の回答 {n} 問を下書きに入れました。「前回から」の印の付いた欄は、今回も当てはまるか確かめてください。直すと印が消えます。まだ送られていません。",
  "obl.prevTag": "前回から",
  "obl.prevStatusNote": "選択は前回の回答です。今回も当てはまるか確かめてください。",
  "obl.reviewPrev": "前回の回答のままの設問が {n} 問あります（「前回から」の印）。今回も当てはまることを確かめてから送ってください。",
  "nav.coverage": "機能対応表",
  "cov.title": "機能対応表（原典 37機能 + G1〜G14）",
  "cov.hint": "レビュー用。原典「業務システム 機能一覧」の No. から該当画面へ飛べます。「外部待ち」はテナント情報・AWS認証・SES解除・IPA様式の公表が要るものです。",
  "cov.function": "機能",
  "cov.status": "状態",
  "cov.note": "備考",
  "cov.open": "開く",
  "cov.s.DONE": "実装済",
  "cov.s.PARTIAL": "一部",
  "cov.s.NOT_BUILT": "作らない",
  "cov.s.EXTERNAL": "外部待ち",
  "login.sso": "{name} でログイン",
  "login.ssoOr": "または、ローカルアカウントで:",
  "login.oidc.NOT_PROVISIONED": "この IdP のアカウントはまだ登録されていません。管理者に利用者登録を依頼してください。",
  "login.oidc.EMAIL_UNVERIFIED": "IdP 側でメールアドレスが検証されていません。",
  "login.oidc.TENANT_MISMATCH": "会社のアカウントでログインしてください（別ドメイン／別テナント）。",
  "login.oidc.USER_NOT_ACTIVE": "このアカウントは停止中または退職済みです。",
  "login.oidc.IDENTITY_CONFLICT": "同じ利用者に別の ID が既に結び付いています。管理者に連絡してください。",
  "login.oidc.FAILED": "SSO でログインできませんでした。もう一度お試しください。",
  // ── 通知 ──
  "notif.title": "通知",
  "notif.empty": "通知はありません。",
  "notif.readAll": "すべて既読にする",
  "notif.bellLabel": "通知（未読 {n} 件）",
  "notif.kind.other": "お知らせ",
  "notif.kind.OBLIGATION_REMINDER": "期限の督促",
  "notif.kind.OBLIGATION_UNASSIGNED": "担当者未定",
  "notif.kind.TRAINING_REMINDER": "未受講",
  "notif.kind.REQUEST_PENDING_APPROVAL": "承認の依頼",
  "notif.kind.REQUEST_APPROVER_UNRESOLVED": "承認者が未定",
  "notif.kind.REQUEST_DECIDED": "申請の結果",
  "notif.kind.REQUEST_APPLIED": "申請の実施",
  "notif.kind.INSPECTION_RESPONSE_REQUESTED": "年次点検の回答依頼",
  "notif.kind.UNUSED_ID_CANDIDATES": "未使用ID",
  "set.title": "自分の設定",
  "set.password": "パスワード",
  "set.currentPw": "現在のパスワード",
  "set.newPw": "新しいパスワード",
  "set.newPwAgain": "新しいパスワード（確認）",
  "set.changePw": "パスワードを変更する",
  "set.pwChanged": "変更しました。",
  "set.pwWrongCurrent": "現在のパスワードが違います",
  "set.pwSame": "現在と同じパスワードには変更できません",
  "set.pwPolicy": "パスワードがポリシーを満たしていません",
  "set.pwMismatch": "確認用と一致しません",
  "totp.gate.title": "多要素認証の登録が必要です",
  "totp.gate.body": "ローカルアカウント（メール+パスワード）でログインする人は、多要素認証（TOTP）を登録するまで他の画面を使えません。下の手順で登録してください。登録が終わると自動でいつもの画面に戻ります。",
  "set.totp": "多要素認証（TOTP）",
  "set.totpOn": "有効です",
  "set.totpOff": "未設定です",
  "set.totpHint": "設定すると、ログイン時にパスワードに加えて認証アプリの6桁のコードが必要になります。先にスマートフォンへ認証アプリ（Google Authenticator、Microsoft Authenticator など）を入れてください。",
  "set.totpBegin": "登録を始める",
  "set.totpS1": "認証アプリで「＋」を押し、「QRコードをスキャン」を選んで、下のQRコードを読み取ります。",
  "set.totpS2": "アプリに表示された6桁の数字を下の欄に入力し、「検証して有効にする」を押します。",
  "set.totpNote": "コードを確認するまでは有効になりません。途中でやめても、ログインできなくなることはありません。",
  "set.totpKeepSecret": "このQRコードとキーは他人に見せず、スクリーンショットにも残さないでください。",
  "set.totpInvalid": "コードが一致しません。アプリに表示されている新しいコードで入力し直してください。何度も失敗する場合は、スマートフォンの日付と時刻を自動設定にしてください。",
  "set.totpPhoneChange": "スマートフォンを替えるときは、先にここで解除してください。新しい端末で登録し直せます。",
  "set.totpCantScan": "QRコードを読み取れない場合",
  "set.totpManual": "アプリで「セットアップキーを入力」（アプリによっては「コードを手動で入力」）を選び、次のキーを入力してください。種類を聞かれたら「時間ベース」を選びます。",
  "set.totpQrAlt": "認証アプリで読み取るQRコード",
  "set.totpConfirm": "検証して有効にする",
  "set.totpEnabled": "多要素認証を有効にしました。",
  "set.totpDisable": "解除する",
  "set.totpDisableReason": "解除の理由（記録に残ります）",
  "set.totpDisabled": "解除しました。",
  "set.policyTitle": "適用中のパスワードポリシー",
  "set.policyLen": "{n}文字以上。",
  "set.policyMixed": "英大文字・小文字・数字を含むこと。",

  // フォーム
  "form.addRow": "行を追加",
  "form.deleteRow": "削除",
  "form.minRows": "1行以上入力してください",
  "form.unsupported": "この画面で編集できない項目があります: {fields}",
  "form.requiredLegend": "* は必須項目です",
  "form.missing": "必須項目が入力されていません: {fields}",
};

const en: Dict = {
  "app.title": "SCS-Hub",
  "app.subtitle": "SCS Level ★3 operations",
  "nav.dashboard": "Readiness",
  "nav.registers": "Registers",
  "nav.requests": "Requests",
  "nav.obligations": "Deadlines",
  "common.loading": "Loading…",
  "common.back": "Back",
  "common.none": "Nothing to show.",
  "common.logout": "Sign out",
  "common.required": "required",
  "common.forbidden": "Your roles do not allow this action (see the Roles page)",
  "common.loadFailed": "Could not load this. Reload the page; if it keeps happening, contact the security team.",
  "common.forbiddenView": "You do not have permission to view this. Ask the security team if you need access.",
  "common.doneBy": "This is done by {roles}.",
  "common.showDetail": "Show details",
  "common.hideDetail": "Hide details",
  "common.select": "(choose one)",
  "common.others": "{n} more",
  "common.calculating": "Calculating…",
  "viewpoint.pinned": "Showing records as known at {time}",
  "viewpoint.reset": "Show latest",
  "viewpoint.hint": "Only records written before this moment are shown. If someone else adds data while you browse, what you see stays consistent across screens.",

  "login.title": "Sign in to SCS-Hub",
  "login.email": "Email address",
  "login.password": "Password",
  "login.totp": "Authenticator code (6 digits)",
  "login.submit": "Sign in",
  "login.submitting": "Checking…",
  "login.err.credentials": "The email address or password is incorrect. If you are stuck, contact the security department head.",
  "login.err.totpRequired": "Enter the code from your authenticator app",
  "login.err.totpInvalid": "That code is not correct. Enter the 6 digits currently shown in your authenticator app.",
  "login.err.network": "Could not reach the server. Your input is kept. Check your connection and press “Sign in” again.",
  "login.totpPrompt": "Enter the 6-digit code shown in your authenticator app.",
  "login.expired": "Your session has timed out. Please sign in again — you will return to the same screen.",
  "review.heading": "This is a review environment. It is not production.",
  "review.body": "It is exposed to the internet, so do NOT enter real confidential information, personal data, or credentials. Anything entered here is used only to check business requirements and will not be carried over to production.",
  "review.deployment": "Deployment",

  "dash.title": "Application readiness",
  "dash.asOf": "As-of date",
  "dash.asOfHint": "Changing this date recounts everything as it stood on that day. What you submit is the state on this date.",
  "dash.criteria": "★3 criteria: {n} items",
  "dash.linked": "Has evidence linked",
  "dash.resolved": "Evidence actually resolves",
  "dash.twoNumbers": "The gap between these two numbers is the point.",
  "dash.twoNumbersBody": "Having evidence linked is not enough: if the register is empty as of the baseline date, it still does not count. The number below is the one that matters for filing.",
  "dash.deadlines": "Inspection & reporting deadlines",
  "dash.overdue": "{n} overdue",
  "dash.dueSoon": "{a} due within 30 days / {b} open",
  "dash.blocked": "Stalled requests",
  "dash.blockedNone": "No requests are stalled waiting for an approver.",
  "dash.blockedSome": "{n} request(s) stalled because no approver is assigned.",
  "dash.blockedDecide": "needs a named holder",
  "dash.gaps": "Criteria still missing evidence",
  "dash.noEvidence": "No evidence linked ({n})",
  "dash.noEvidenceHint": "Most of these are satisfied by policy documents, training records or configuration screenshots rather than registers.",
  "dash.emptyEvidence": "Linked but empty ({n})",
  "dash.emptyEvidenceHint1": "Putting data into the register resolves these. ",
  "dash.emptyEvidenceHint2": "showing “evidence exists” when it is empty",
  "dash.emptyEvidenceHint3": " is exactly what we count separately to prevent.",
  "dash.package": "Submission package",
  "dash.packageHint1": "Bundles the self-assessment, evidence, registers and request records as of the base date into one ZIP. With the same base date, ",
  "dash.packageHint2": "the contents are always the same",
  "dash.packageHint3": ".",
  "dash.packagePreview": "Preview contents",
  "dash.packageFiles": "{n} files / {bytes} bytes",
  "dash.packageProblems": "To resolve before submission: {n}",
  "dash.packageFrozen": "The first finalized export is what gets frozen as the submission.",

  "obl.title": "Inspection & reporting deadlines",
  "obl.all": "All",
  "obl.due": "Due",
  "obl.status": "Status",
  "obl.severity": "Severity",
  "obl.content": "Item",
  "obl.submitTo": "Submit to",
  "obl.criteria": "Criteria",
  "obl.complete": "Complete",
  "obl.waive": "Waive",
  "obl.waivePrompt": "Reason for waiving (the justification becomes part of the evidence)",
  "obl.waiveReasonLabel": "Reason for waiving (the justification becomes part of the evidence)",
  "obl.waiveSubmit": "Waive",
  "obl.waiveConfirm": "Permanently waive “{title}” (due {date})?",
  "obl.filterLabel": "Filter by status",
  "obl.completeConfirm": "Permanently record “{title}” (due {date}) as done?",
  "obl.sev.NORMAL": "Normal",
  "obl.sev.HIGH": "High",
  "obl.sev.CRITICAL": "Critical",
  "obl.st.OVERDUE": "Overdue",
  "obl.st.SCHEDULED": "Scheduled",
  "obl.st.OPEN": "In progress",
  "obl.st.COMPLETED": "Completed",
  "obl.st.WAIVED": "Waived",
  "obl.st.SUBMITTED": "Submitted",
  "dash.manifest": "Content check value (SHA-256)",

  "req.title": "Requests",
  "req.new": "New request",
  "req.tab.pending": "Awaiting my action (approval / implementation)",
  "req.tab.mine": "Submitted by me",
  "req.tab.all": "All",
  "req.no": "Reference no.",
  "req.type": "Type",
  "req.state": "State",
  "req.submitted": "Submitted",
  "req.steps": "Approval steps",
  "req.empty": "No matching requests.",
  "req.blockedHeading": "{n} request(s) stalled because no approver is assigned.",
  "req.blockedNeeds": "needs a named holder to proceed",
  "req.originRatio": "Of {total} register updates in the last year, {n} came from approved requests ({pct}%).",
  "req.originRatioRest": "The rest are manual updates, each with a recorded reason.",
  "req.originRatioHint": "Assessors ask whether register updates went through approval. Being able to measure it matters more than the number being zero.",
  "req.kind": "Request type",
  "req.route": "Approval route",
  "req.conditional": "conditional",
  "req.basis": "Basis",
  "req.effectiveDate": "Effective date (optional)",
  "req.effectiveDateHint": "If left blank, the register is updated from the day of approval (or of the implementation record, where one is required). With a future date, the change appears in the register only from that day, so you can file transfers or departures in advance.",
  "req.submit": "Submit",
  "req.submitting": "Submitting…",
  "req.submitNote": "On submission a reference number is assigned and never changes.",
  "req.payload": "Request details",
  "req.effect": "What approval will change in the registers",
  "req.effectNone": "This type does not update a register (only the request and approval records remain).",
  "req.col.field": "Field",
  "req.col.before": "Current",
  "req.col.after": "After approval",
  "req.separation": "Segregation of duties warning",
  "req.sepSame": "The requester and the approver are the same person.",
  "req.sepMulti": "One person holds more than one approval step.",
  "req.sepBody": "This request can be submitted. However, the approver must enter both ",
  "req.sepBodyStrong": "“Why duties cannot be separated” and a “Compensating control” before approving.",
  "req.sepReason": "Why duties cannot be separated",
  "req.sepReasonPh": "e.g. no one else in the department can approve",
  "req.sepControl": "Compensating control",
  "req.sepControlPh": "e.g. the CEO reviews approval results every quarter",
  "req.sepEvidence": "What you write here is submitted to the assessor as ",
  "req.sepEvidenceStrong": "proof that a control is in place",
  "req.sepEvidenceRest": ".",
  "req.history": "History",
  "req.decision": "Decision (step {n} / {role})",
  "req.decisionHelp": "Send back: ask the requester to correct it (they submit again). Reject: refuse and close it. Neither can be undone.",
  "req.waitingFor": "Awaiting approval: step {n} / {role} ({who})",
  "req.comment": "Comment (required when sending back or rejecting; the requester will see it)",
  "req.approve": "Approve",
  "req.return": "Send back",
  "req.reject": "Reject",
  "req.applied": "Implementation record",
  "req.appliedHint": "For this type, approval alone does not update the register. Record it after the work is done and the work report has been received (the implementation record required by 4-1-1-1).",
  "req.appliedField": "What was done / report received",
  "req.appliedSubmit": "Record implementation and update the register",
  "req.waitingApply": "Approved. The register is updated once the person responsible finishes the work and records it.",
  "req.withdraw": "Withdraw",
  "req.withdrawNote": "Withdrawing keeps the record (nothing is ever deleted).",
  "req.withdrawReason": "Reason for withdrawing (optional)",
  "req.returnedHelp": "This request was sent back. Read the comment in the history below, correct the details and submit it again from “New request”. Close this one with “Withdraw” (the record is kept).",
  "req.unresolvedRole": "This request cannot proceed because no one holds the approver role. Assign a person to {roles}.",
  "req.applicant": "Requested by",
  "req.col.step": "Step",
  "req.col.role": "Role",
  "req.col.decidedBy": "Decided by",
  "req.col.decidedAt": "Decided at",
  "req.col.comment": "Comment",
  "req.notFound": "This request does not exist or you do not have permission to view it.",
  "req.confirmApproveFinal": "Approving will apply the changes shown under “What approval will change in the registers” (for types that need an implementation record, it applies once that is recorded). Approval cannot be undone. Approve?",
  "req.confirmApproveStep": "Approving passes this request to the next step ({role}). This cannot be undone. Approve?",
  "req.confirmReturn": "Send this request back to the requester? They will need to correct it and submit again.",
  "req.confirmReject": "Rejecting closes this request permanently. Reject?",
  "req.confirmWithdraw": "Withdraw this request? A withdrawn request cannot be reopened (the record is kept).",
  "req.confirmApply": "Recording the implementation updates the register and cannot be undone. Record it?",
  "req.ev.SUBMIT": "Submitted",
  "req.ev.APPROVE": "Approved",
  "req.ev.REJECT": "Rejected",
  "req.ev.RETURN": "Sent back",
  "req.ev.WITHDRAW": "Withdrawn",
  "req.ev.APPLY": "Implementation recorded",
  "step.APPROVED": "approved",
  "step.REJECTED": "rejected",
  "step.RETURNED": "sent back",
  "step.PENDING": "pending",
  "step.unassigned": "no approver",
  "req.empty.pending": "Nothing is waiting for your approval or implementation.",
  "req.empty.mine": "You have not submitted any requests yet. Use “New request” to create one.",
  "req.err.ack": "The requester and approver are the same person. Enter both a reason and a compensating control.",
  "req.err.stale": "The target register was updated through another path after you submitted. Review the change and file again.",
  "req.err.forbidden": "You do not have permission to approve this request",
  "req.err.badState": "This request has already been decided",
  "req.err.generic": "Could not complete the action",
  "req.err.payload": "Please check the values you entered",
  "req.err.noRoute": "No approval route is configured for this type. Contact an administrator.",
  "req.err.separation": "The requester and approver are the same person, which this type does not allow",
  "req.err.submit": "Could not submit",

  "state.DRAFT": "Draft",
  "state.SUBMITTED": "Submitted",
  "state.IN_REVIEW": "In review",
  "state.APPROVED": "Approved (not yet applied)",
  "state.APPLIED": "Applied",
  "state.RETURNED": "Sent back",
  "state.WITHDRAWN": "Withdrawn",
  "state.REJECTED": "Rejected",
  "state.EXPIRED": "Expired",


  // Incidents
  "nav.incidents": "Incidents",
  "inc.title": "Security incidents",
  "inc.report": "Report a discovery",
  "inc.reportHint": "Phone first. Once you have reached someone, record it here (procedure #08). A reference number and receipt time are assigned automatically and cannot be changed later.",
  "inc.callWho": "Call: {roleName} {name} (deputy: {deputy})",
  "inc.callWhoVacant": "No incident intake contact is assigned. Contact the security department.",
  "inc.receivedDone": "Received. Reference no.: {no} ({time}). Quote this number in any follow-up.",
  "inc.listRestricted": "Only the incident team can see the list. Your report was received under the reference number shown when you submitted it.",
  "inc.detectedAt": "When it was discovered",
  "inc.detectedAtLocalHint": "your computer's local time",
  "inc.summary": "What happened",
  "inc.summaryPh": "e.g. received a sign-in notification I do not recognise on my work PC",
  "inc.route": "How it was detected",
  "inc.routePh": "e.g. UTM alert / noticed by the user / reported by a vendor",
  "inc.assets": "Affected assets",
  "inc.personal": "Personal data may be involved",
  "inc.personalHint": "If unsure, say yes. This is the entry point for deciding on external reporting.",
  "inc.vendorOrg": "If reported by a vendor, which organisation",
  "inc.submit": "Report",
  "inc.no": "Reference no.",
  "inc.stage": "Stage",
  "inc.detected": "Discovered",
  "inc.received": "Received",
  "inc.advance": "Advance stage",
  "inc.advanceHint": "Stages cannot go back. If something needs correcting, contact the security team.",
  "inc.initialAction": "Initial response taken",
  "inc.cause": "Cause",
  "inc.preventive": "Preventive measures",
  "inc.extReport": "External reporting decision",
  "inc.extReportHint": "Record a decision NOT to report as well. Without it, next year's inspection cannot tell a decision from an oversight.",
  "inc.decisionLabel": "External report",
  "inc.saveDecision": "Record decision",
  "inc.err.reason": "Enter the reason for the decision. A reason is required even if you decide not to report.",
  "inc.advanceConfirm": "Move to “{next}”? Stages cannot go back.",
  "inc.reportedConfirm": "Record {label} as reported? This cannot be undone.",
  "inc.dest": "Report to",
  "inc.required": "Report",
  "inc.notRequired": "Do not report",
  "inc.reason": "Reason for the decision",
  "inc.phase": "Phase",
  "inc.phasePrelim": "Preliminary",
  "inc.phaseDefinitive": "Definitive",
  "inc.malicious": "May involve malicious intent",
  "inc.due": "Due",
  "inc.noDue": "No statutory deadline (promptly)",
  "inc.markReported": "Mark as reported",
  "inc.judgeConfirm": "Record this external-report decision ({required})?",
  "inc.pendingReports": "External reports not yet made",
  "inc.overdue": "overdue",
  "inc.empty": "No incidents have been recorded.",

  // Training
  "nav.training": "Training",
  "trn.title": "Security training",
  "trn.sessions": "Sessions",
  "trn.open": "New session",
  "trn.openConfirm": "This will register the session. The list of assigned learners becomes fixed once it is created. Continue?",
  "trn.course": "Course",
  "trn.sessionTitle": "Session name",
  "trn.method": "Delivery",
  "trn.elearning": "E-learning",
  "trn.classroom": "Classroom",
  "trn.scheduledOn": "Date",
  "trn.targetNote": "Who it is for (note)",
  "trn.assigned": "Assigned",
  "trn.completed": "Completed",
  "trn.exempt": "Exempt",
  "trn.rate": "Completion rate",
  "trn.notMeasurable": "not measurable",
  "trn.take": "Record my completion",
  "trn.score": "Quiz score (0-100)",
  "trn.scoreHint": "If you do not reach the pass mark, it is not counted as completed and you stay on the reminder list.",
  "trn.passed": "Passed",
  "trn.failed": "Below the pass mark. Please take the course again.",
  "trn.recorded": "Completion recorded.",
  "trn.pending": "Not yet completed",
  "trn.remind": "Show reminder targets",
  "trn.empty": "No sessions yet.",
  "trn.err.notAssigned": "You are not on the list for this session, so you cannot record completion. If you should be, contact the training coordinator.",
  "trn.err.noTarget": "There are no trainees, so the session cannot be opened. Ask the system administrator to check which users are marked as training targets.",
  "trn.passMark": "Pass mark: {n}",
  "trn.material": "Open material",
  "trn.my.assigned": "You are assigned (not completed)",
  "trn.my.completed": "Completed",
  "trn.my.notAssigned": "You are not assigned to this session",
  "trn.closed": "This session is closed",
  "trn.assignedNote": "Assignment is frozen at the time the session opens. People who join later are not counted in the denominator.",


  // Criteria & evidence
  "nav.criteria": "Criteria",
  "cri.title": "Criteria and evidence",
  "cri.progress": "Of the {total} ★3 criteria, {linked} have evidence linked and {resolved} resolve to something real.",
  "cri.filter.gap": "No evidence",
  "cri.filter.empty": "Linked but empty",
  "cri.filter.all": "All",
  "cri.noneLeft": "Nothing matches this filter.",
  "cri.code": "Criterion",
  "cri.deliverable": "Required output",
  "cri.evidence": "Evidence",
  "cri.noLink": "not linked",
  "cri.relatedRegisters": "Related registers",
  "cri.assess": "Self-assessment",
  "cri.conclusion": "Conclusion",
  "cri.c.MET": "Met",
  "cri.c.PARTIAL": "Partially met",
  "cri.c.NOT_MET": "Not met",
  "cri.c.NOT_APPLICABLE": "Not applicable",
  "cri.rationale": "Rationale (why this holds)",
  "cri.rationalePh": "e.g. Register form 3 tracks OS and manufacturer for every device, reviewed quarterly",
  "cri.rationaleHint": "Evidence alone does not show why you are compliant. This sentence goes straight into the self-assessment sheet.",
  "cri.naJust": "Why it does not apply",
  "cri.naJustPh": "e.g. no shared IDs are issued at all (form 11 does not apply)",
  "cri.saveAssessment": "Save assessment",
  "cri.addEvidence": "Add evidence",
  "cri.addEvidenceHint": "Point at where a policy document, training record or configuration screenshot lives. Criteria backed by a register resolve automatically once the register has data.",
  "cri.evidenceTitle": "Evidence name",
  "cri.evidenceTitlePh": "e.g. Information Security Basic Policy (intranet)",
  "cri.evidenceUrl": "URL",
  "cri.linkEvidence": "Link to this criterion",
  "cri.done": "Saved.",
  "cri.noCycle": "No assessment cycle exists yet. Create one first.",
  "cri.cycleOrder": "Order defined by SCS-100 3.1.1: (1) self-assessment, (2) confirmation and signature by the ★3 confirmer, (3) declaration of conformity, (4) application to IPA, (5) registration. Steps cannot be skipped.",
  "cri.ready": "Ready to declare.",
  "cri.notReady": "{n} thing(s) block the declaration.",
  "cri.startConfirm": "Send for confirmer review",
  "cri.startConfirmConfirm": "Send {label} for ★3 confirmer review?",
  "cri.confirmerName": "Name of the ★3 confirmer",
  "cri.confirmerQual": "Qualification (one of the six, plus the training)",
  "cri.confirmerRegNo": "Registration no. (optional)",
  "cri.confirmerAffiliation": "Affiliation",
  "cri.affiliation.INTERNAL": "Internal",
  "cri.affiliation.EXTERNAL": "External",
  "cri.sign": "Sign as confirmer",
  "cri.signConfirm": "Record your signature as the ★3 confirmer for {label}? A signature cannot be withdrawn.",
  "cri.signedMsg": "Signed.",
  "cri.declare": "Declare conformity (executive)",
  "cri.declareConfirm": "Make the declaration of conformity for {label}? It is recorded as management's declaration and cannot be undone.",
  "cri.markSubmitted": "Mark as applied to IPA",
  "cri.submittedConfirm": "Do this only after you have actually applied to IPA. It is recorded as applied and cannot be undone. Continue?",
  "cri.statusChanged": "Status is now “{status}”.",
  "cri.markRegistered": "Record registration",
  "cri.registeredAt": "Registration notice date (as stated in IPA's notice)",
  "cri.registeredHint": "Next year's renewal deadline is calculated from this date.",
  "cri.registeredConfirm": "Record {date} as the registration notice date? This cannot be undone.",
  "cri.current": "Saved self-assessment: {conclusion} ({date}, {who})",
  "cri.notAssessed": "Not assessed yet",
  "cri.err.conclusion": "Choose a conclusion.",
  "cri.err.rationale": "Enter the rationale.",
  "cri.addComment": "Record a confirmer finding",
  "cri.commentPh": "e.g. the rationale does not state how often the inventory is taken",
  "cri.confirmerCommentLabel": "Confirmer finding",
  "cycle.st.OPEN": "Self-assessment in progress",
  "cycle.st.CONFIRMING": "Under confirmer review",
  "cycle.st.DECLARED": "Conformity declared",
  "cycle.st.SUBMITTED": "Applied to IPA",
  "cycle.st.REGISTERED": "Registered",


  // Settings
  "nav.settings": "Settings",
  // ── Registers (operations only; register names, form numbers, column labels, confidence and values stay in Japanese) ──
  "reg.ledgers": "Registers ({n})",
  "reg.records": "Records ({n})",
  "reg.noForm": "no form",
  "reg.imported": "imported",
  "reg.importedHint": "populated from an external source",
  "reg.restricted": "Only people in the responsible roles can view this",
  "reg.restrictedLabel": "Restricted",
  "reg.notApplicable": "Not applicable: {reason}",
  "reg.listError": "Could not load the register list",
  "reg.schemaError": "Could not load the register definition",
  "reg.loadError": "Could not load the register",
  "reg.backToList": "← All registers",
  "reg.criteria": "Related criteria:",
  "reg.asOf": "Show the state as of this date",
  "reg.includeDeleted": "Include retired rows",
  "reg.reload": "Reload",
  "reg.addRow": "Add row",
  "reg.csv": "CSV (as of this date)",
  "reg.importCsv": "Import CSV",
  "reg.importing": "Importing…",
  "reg.importDone": "Imported: {c} new / {u} updated / {s} unchanged / {e} errors / {o} not in source",
  "reg.onlyInSystem": "Not in the source (kept; append a DELETE version if disposed)",
  "reg.importFailed": "Import failed",
  "reg.importConfirm": "Import {file}? Rows will be recorded as observed on {date}. Imported versions cannot be undone. Continue?",
  "reg.importPastDate": "The “as of” date is not today ({date}). Continue only if you mean to import with that past date.",
  "reg.importHint": "Use the same columns as a file exported with “CSV (as of this date)”. Rows with no change do not create a new version.",
  "reg.forbidden": "You do not have permission to view this register. Ask the security team if you need access.",
  "reg.count": "{n} rows.",
  "reg.asOfHint1": "Pick a past date and you see the register exactly as it stood then.",
  "reg.col.key": "Key",
  "reg.col.status": "Status",
  "reg.col.validFrom": "Effective",
  "reg.col.history": "History",
  "reg.generation": "gen. {n}",
  "reg.retired": "retired",
  "reg.active": "active",
  "reg.history": "History",
  "reg.emptyAsOf": "No rows as of this date",
  "reg.yes": "yes",
  "reg.no": "no",
  "reg.add.title": "Add row",
  "reg.add.hint": "Adds a new row. Entries are never overwritten; corrections are kept as new versions.",
  "reg.add.key": "Key (asset no., etc.)",
  "reg.add.keyExists": "This key already exists. To correct it, use “Correct this row” in the list (saving here leaves every column you did not fill in empty).",
  "reg.add.multiHint": "Separate multiple values with commas",
  "reg.add.errJson": "{label} is not valid JSON",
  "reg.add.derivedUnset": "(not set)",
  "reg.add.boolUnset": "(not answered)",
  "reg.add.validFrom": "Effective date",
  "reg.add.note": "Reason for change",
  "reg.add.noteHint": "(required — so that “why” can be explained later)",
  "reg.add.saving": "Saving…",
  "reg.add.submit": "Save",
  "reg.add.cancel": "Cancel",
  "reg.add.errSchema": "The input does not match the register definition",
  "reg.add.errBody": "Required fields are missing (the reason for change is required)",
  "reg.add.errSave": "Could not save",
  "reg.edit": "Correct this row",
  "reg.editTitle": "Correct {key}",
  "reg.editHint": "The form opens with the current values. The key cannot be changed. What you enter becomes the new version in full (every column is saved again, not just the ones you touch).",
  "reg.retire": "Retire",
  "reg.retireTitle": "Retire {key}",
  "reg.retireConfirm": "Retire {key} as of {date}? The history is kept and this cannot be undone.",
  "reg.schemaErr.required": "{label} is required",
  "reg.schemaErr.enum": "Choose {label} from the list",
  "reg.schemaErr.format": "{label} is not in the correct format",
  "reg.hist.title": "History of {key} ({n} versions)",
  "reg.hist.hint": "Versions are append-only; they cannot be edited or deleted (enforced by the database).",
  "reg.hist.seq": "Ver.",
  "reg.hist.op": "Operation",
  "reg.hist.recordedAt": "Recorded at",
  "reg.hist.source": "Source",
  "reg.hist.note": "Reason",
  "reg.hist.data": "Data",
  "reg.hist.close": "Close",
  "reg.op.CREATE": "created",
  "reg.op.UPDATE": "updated",
  "reg.op.DELETE": "retired",
  // ── Roles ──
  "nav.roles": "Roles",
  "roles.title": "Roles and people",
  "roles.hint": "If a role has no holder, requests that need its approval stop and ★3 preparation stalls. Name a deputy as well, for holidays and leavers.",
  "roles.whoAssigns": "Only the Security Executive and the Head of the Security Department can assign people.",
  "roles.vacantCount": "{n} vacant",
  "roles.noDeputyCount": "{n} without deputy",
  "roles.loadError": "Could not load roles",
  "roles.col.role": "Role",
  "roles.col.primary": "Primary",
  "roles.col.deputy": "Deputy",
  "roles.col.fallback": "Fallback approver",
  "roles.col.kind": "Kind",
  "roles.col.holder": "Holder",
  "roles.scoped": "One holder per system or information asset",
  "roles.vacant": "Not assigned",
  "roles.noDeputyWarn": "Name a deputy",
  "roles.assignPrimary": "Set primary",
  "roles.assignDeputy": "Set deputy",
  "roles.history": "History",
  "roles.historyTitle": "Assignment history of {code}",
  "roles.historyHint": "Assignments are append-only. A handover is a new row; who approved what in the past never changes.",
  "roles.form.titlePrimary": "Primary for {role}",
  "roles.form.titleDeputy": "Deputy for {role}",
  "roles.form.user": "Person",
  "roles.form.choose": "(choose a person)",
  "roles.form.vacate": "Leave this role vacant",
  "roles.vacateConfirm": "Make the {kind} of {role} vacant? Requests waiting on this role will stop until someone is assigned. Continue?",
  "roles.form.validFrom": "Effective date",
  "roles.form.note": "Basis",
  "roles.form.notePh": "e.g. appointment letter dated 2026-09-15",
  "roles.form.submit": "Assign",
  "roles.saveError": "Could not save",
  "dash.roles": "Roles",
  "dash.rolesVacant": "{n} roles without a named person",
  "dash.rolesOk": "All 17 roles have a named person.",
  "dash.rolesNoDeputy": "{n} without deputy",
  "dash.rolesOpen": "Assign roles",
  "cri.addFile": "Add a file as evidence",
  "cri.addFileHint": "Screenshots of settings, etc. The file is pinned by hash and version at upload, so a later replacement never changes past packages. Check that no one else’s name or e-mail is visible before uploading.",
  "cri.file": "File",
  "cri.capturedAt": "Captured on",
  "cri.targetSystem": "Target system",
  "cri.targetSystemPh": "e.g. Google Workspace admin console",
  "cri.uploading": "Uploading…",
  "cri.uploadAndLink": "Upload and link",
  "dash.finalize": "Finalize package",
  "dash.finalizeHint": "Stores the ZIP where it cannot be deleted. A “Submission” package cannot be deleted by anyone, including administrators, for 5 years.",
  "dash.finalizeRun": "Finalize",
  "dash.finalizeConfirm": "Finalize the state as of {asOf} as a “Submission” package? Nobody, including administrators, can delete or change it for 5 years. Proceed?",
  "dash.finalizeDone": "Finalized (check value {hash}…)",
  "dash.finalizeFailed": "Could not finalize",
  "dash.purpose.DRAFT": "Draft",
  "dash.purpose.CONFIRMER_REVIEW": "Confirmer review",
  "dash.purpose.SUBMISSION": "Submission",
  "dash.ex.when": "Exported at",
  "dash.ex.purpose": "Purpose",
  "dash.ex.purposeLabel": "Purpose",
  "dash.ex.status": "Status",
  "dash.ex.lock": "Retained until",
  "dash.ex.by": "By",
  "dash.exst.RUNNING": "Running",
  "dash.exst.READY": "Awaiting verification",
  "dash.exst.VERIFIED": "Finalized",
  "dash.exst.FAILED": "Failed",
  "dash.oblOpen": "Open deadlines",
  // ── Governance ──
  "nav.governance": "Governance",
  "gov.title": "Governance",
  "gov.tab.rules": "Parameters",
  "gov.tab.documents": "Policy documents",
  "gov.tab.corrective": "Corrective actions",
  "gov.tab.audit": "Audit log",
  "gov.rules.hint": "Values fixed by the scheme cannot be changed. Values with a limit set by the scheme (such as “at least once a year”) can only be made stricter. Values the company decides can be changed, and every change is kept in the history.",
  "gov.rules.name": "Parameter",
  "gov.rules.value": "Value",
  "gov.rules.range": "Range",
  "gov.rules.source": "Basis",
  "gov.rules.change": "Change",
  "gov.rules.note": "Reason",
  "gov.rules.save": "Append new version",
  "gov.rules.err.number": "Enter a number (unit: {unit}).",
  "gov.rules.err.format": "The value is not in the expected format. Please re-enter each item.",
  "gov.docs.hint": "Establishment date, revision history and approval record of the 11 documents. Versions are append-only.",
  "gov.docs.title": "Document",
  "gov.docs.kind": "Kind",
  "gov.docs.current": "Current",
  "gov.docs.established": "Established / revised",
  "gov.docs.approved": "Approval",
  "gov.docs.none": "no version",
  "gov.docs.addVersion": "Add version",
  "gov.docs.addVersionTitle": "Add a version to document {docNo}",
  "gov.docs.version": "Version",
  "gov.docs.revisedAt": "Revised on",
  "gov.docs.approvedBy": "Approving role",
  "gov.docs.approvedAt": "Approved on",
  "gov.docs.file": "File (optional)",
  "gov.docs.changeNote": "What changed",
  "gov.docs.save": "Append",
  "gov.ca.hint": "Findings from inspections and the confirmer’s review. Carrying an open finding into the next cycle creates a linked new row — nothing disappears silently.",
  "gov.ca.openCount": "{n} open",
  "gov.ca.add": "Open a finding",
  "gov.ca.editDue": "Change due date / note",
  "gov.ca.dueTitle": "Change the due date of a corrective action",
  "gov.ca.dueCurrent": "Current due date: {date}",
  "gov.ca.dueNew": "New due date",
  "gov.ca.dueReason": "Reason (optional)",
  "gov.ca.dueReasonReq": "Reason (required when moving the date later)",
  "gov.ca.dueLaterNote": "You are moving the due date later. The reason is kept in the action's note and the audit log.",
  "gov.ca.dueMemo": "Note (optional; added to the action's note)",
  "gov.ca.dueNoChange": "Change the due date or add a note",
  "gov.ca.dueReasonNeeded": "Enter a reason to move the due date later",
  "gov.ca.dueSaved": "Due date changed",
  "gov.ca.memoSaved": "Note added",
  "gov.ca.dueSave": "Save change",
  "gov.ca.status": "Status",
  "gov.ca.titleCol": "Title",
  "gov.ca.finding": "Finding",
  "gov.ca.owner": "Owner role",
  "gov.ca.due": "Due",
  "gov.ca.criteria": "Criteria",
  "gov.ca.notePh": "what was done",
  "gov.ca.start": "Start",
  "gov.ca.done": "Done",
  "gov.ca.carryOver": "Carry over",
  "gov.ca.doneConfirm": "Mark this corrective action as done. There is no way back from done. Continue?",
  "gov.ca.carryOverConfirm": "Carry this corrective action over to the next cycle. There is no way back from carried-over. Continue?",
  "gov.ca.carried": "carried over",
  "gov.ca.save": "Open",
  "gov.ca.s.OPEN": "Open",
  "gov.ca.s.IN_PROGRESS": "In progress",
  "gov.ca.s.DONE": "Done",
  "gov.ca.s.CARRIED_OVER": "Carried over",
  "gov.audit.hint": "A record of who did what and when, stored so that it cannot be altered later. Opening this page is also recorded.",
  "gov.audit.filterPh": "filter by action (e.g. auth. / register. / access.denied)",
  "gov.audit.verify": "Verify chain",
  "gov.audit.chainOk": "Chain intact ({n} rows)",
  "gov.audit.chainBroken": "The log chain is broken at id {id}. This may indicate tampering or a fault. Contact the Security Executive immediately.",
  "gov.audit.outcome.SUCCESS": "Success",
  "gov.audit.outcome.DENIED": "Denied",
  "gov.audit.outcome.ERROR": "Error",
  "gov.audit.anchors": "{n} daily anchors in S3",
  "gov.audit.when": "When",
  "gov.audit.actor": "Actor",
  "gov.audit.action": "Action",
  "gov.audit.target": "Target",
  "gov.audit.outcome": "Outcome",
  "gov.tab.vendors": "Vendor inquiries",
  "gov.tab.users": "Users",
  "gov.vd.hint": "38 inquiries to vendors. For each criterion you can see whose answer is pending. Register each answer you receive (date, period, file) in “委託先証跡の受領記録” with the inquiry No.",
  "gov.vd.evidenceLinkLabel": "Vendor evidence received (register)",
  "gov.vd.summary": "{pending} pending / {total}",
  "gov.vd.all": "All vendors",
  "gov.vd.vendor": "Vendor",
  "gov.vd.question": "Inquiry",
  "gov.vd.criteria": "Criteria",
  "gov.vd.status": "Status",
  "gov.vd.sent": "sent",
  "gov.vd.answered": "answered",
  "gov.vd.received": "{n} evidence received",
  "gov.vd.markSent": "Mark sent",
  "gov.vd.markAnswered": "Mark answered",
  "gov.vd.notNeeded": "Not needed",
  "gov.vd.notNeededConfirm": "Mark this inquiry as not needed. There is no way to undo this. Continue?",
  "gov.vd.whyNotNeeded": "Why this inquiry is not needed (recorded as evidence)",
  "gov.vd.evidenceHint": "Register received evidence in:",
  "gov.us.hint": "Recording a departure automatically drafts a deletion request for each ID the person holds, vacates their roles and ends their sessions. Users are never deleted (they remain as subjects of past records). Erasure requests are handled by pseudonymisation.",
  "gov.us.unused": "{n} unused IDs (no login for {days}+ days)",
  "gov.us.days": "d",
  "gov.us.name": "User",
  "gov.us.status": "Status",
  "gov.us.lastLogin": "Last login",
  "gov.us.s.ACTIVE": "active",
  "gov.us.s.SUSPENDED": "suspended",
  "gov.us.s.RETIRED": "left",
  "gov.us.suspend": "Suspend",
  "gov.us.retire": "Record departure",
  "gov.us.reactivate": "Reactivate",
  "gov.us.pseudonymize": "Pseudonymise",
  "gov.us.pseudonymized": "pseudonymised",
  "gov.us.notePrompt": "Basis for setting {name} to “{status}” (letter, notice…)",
  "gov.us.datePrompt": "Effective date (YYYY-MM-DD)",
  "gov.us.retired": "Departure recorded. Drafted {n} deletion requests and vacated {roles} roles.",
  "gov.us.pseudoPrompt": "Reason to pseudonymise {name} (e.g. erasure request received on…). Irreversible.",
  "gov.us.effectiveDate": "Effective date",
  "gov.us.note": "Basis (letter, notice, etc.)",
  "gov.us.retireConfirm": "Record {name}'s departure as of {date}? Deletion requests are drafted for each of their IDs, their roles are vacated and their sessions are ended. This cannot be undone.",
  "gov.us.statusConfirm": "Change {name} to “{status}” as of {date}?",
  "gov.us.pseudoReason": "Reason to pseudonymise (e.g. erasure request received on…)",
  "gov.us.pseudoConfirm": "Pseudonymise {name}? This cannot be undone.",
  "cri.vendorWaiting": "Vendor inquiries",
  "inc.share": "Share as a case",
  "inc.shareHint": "Turns it into a course with the preventive measure, recorded together with training (function 22).",
  "inc.sharePrompt": "Description for the course (remove personal data and customer names)",
  "inc.shared": "Course {code} created. You can open a session from the Training page.",
  "inc.alreadyShared": "Already shared as a course.",
  "obl.responses": "Dept. responses",
  "obl.responsesTitle": "Department responses",
  "obl.responsesHint": "Each question has a designated answerer: departments answer the departmental questions, and the security team answers the company-wide ones. Every answer needs what you checked and the evidence (Annual Inspection Checklist).",
  "obl.collect": "Request responses from departments",
  "obl.collected": "Requested {n} responses, notified {notified} people.",
  "obl.answeredCount": "{a} / {b} answered",
  "obl.dept": "Answered by",
  "obl.secTeam": "Security team",
  "obl.secTeamNote": "Company-wide questions",
  "obl.round": "Request round",
  "obl.respondedAt": "Answered at",
  "obl.answer": "Answer",
  "obl.pending": "pending",
  "obl.answerBtn": "Answer",
  "obl.q": "Question {n}",
  "obl.as.DONE": "Done",
  "obl.as.PARTIAL": "Partly done",
  "obl.as.NOT_DONE": "Not done",
  "obl.as.NA": "Not applicable",
  "obl.basis": "What you checked and the evidence (document name, date, link, etc.)",
  "obl.basisHint": "Required for every answer. For partly done or not done, also say what is missing",
  "obl.criteriaBasis": "Evaluation criteria this is based on",
  "obl.noQuestions": "There are no questions for you to answer in this request. You can send a note only.",
  "obl.ansSummary": "Done {done} · Partly done {partial} · Not done {notDone} · N/A {na}",
  "obl.ansCorrective": "Partly done and not done answers are raised as corrective actions.",
  "obl.ansCorrectiveLink": "View corrective actions",
  "obl.answerSentCa": "Answer sent. {n} corrective actions were raised.",
  "obl.err.pick": "Question {n}: choose done, partly done, not done or not applicable",
  "obl.err.basis": "Question {n}: enter what you checked and the evidence",
  "obl.err.basisLong": "Question {n}: keep the basis within {max} characters",
  "obl.err.stale": "Question {n}: the questions seem to have changed. Reload the page. {msg}",
  "obl.answerNote": "Note (optional)",
  "obl.collectDue": "Answer by",
  "obl.submitAnswer": "Submit answer",
  "obl.formTitle": "Answer for {who}",
  "obl.progress": "{done} of {total} answered",
  "obl.autosaveHint": "Your input is saved automatically and stays when you close this or reload. It remains a draft until you send it.",
  "obl.draftRestored": "We restored the answer you were filling in. Check it and press “Review answers”.",
  "obl.draftDiscard": "Discard the draft and start over",
  "obl.draftDiscardConfirm": "Discard what you entered and start over?",
  "obl.review": "Review answers",
  "obl.reviewTitle": "Answer for {who}: check before sending",
  "obl.reviewCorrective": "{n} question(s) answered partly done or not done will be registered as corrective actions (due {date}).",
  "obl.reviewIrreversible": "You can't edit the answer after sending. Check it before you send.",
  "obl.reviewSend": "Send this answer",
  "obl.reviewBack": "Go back and edit",
  "obl.hintTitle": "Records in this app",
  "obl.hintAsOf": "(as of {date})",
  "obl.hintPrefix": "[Records in the app {date}] ",
  "obl.hintInsert": "Add to what you checked",
  "obl.hintInserted": "Added",
  "obl.hintOpen": "Open the record",
  "obl.hintNote": "This only shows records the app already holds. Check them yourself and choose the answer (done, etc.).",
  "obl.evTitle": "Evidence links (optional)",
  "obl.evCount": "{n}/{max}",
  "obl.evUrl": "Where the document is kept (URL)",
  "obl.evHint": "Only addresses starting with http:// or https://. After typing, press “Add link”",
  "obl.evName": "Display name (optional)",
  "obl.evAdd": "Add link",
  "obl.evRemove": "Remove link “{name}”",
  "obl.evRemoveShort": "Remove",
  "obl.evLinks": "Evidence links",
  "obl.err.linkUrl": "Question {n}: the link must be a URL starting with http:// or https:// (display name up to 200 characters)",
  "obl.err.linkMany": "Question {n}: up to {max} links",
  "obl.err.linkPending": "Question {n}: the link you typed has not been added. Press “Add link” or clear it",
  "obl.prevOffer": "There is a previous answer ({date}, {who}). You can put {n} of them into your draft and adjust them to the current situation.",
  "obl.prevUse": "Put the previous answers into the draft",
  "obl.prevCopied": "{n} previous answers were put into the draft. Check that each field marked “From last time” still applies; the mark disappears when you edit it. Nothing has been sent yet.",
  "obl.prevTag": "From last time",
  "obl.prevStatusNote": "The choice is last time's answer. Check that it still applies.",
  "obl.reviewPrev": "{n} question(s) are still last time's answer (marked “From last time”). Check that they still apply before you send.",
  "nav.coverage": "Coverage",
  "cov.title": "Function coverage (37 functions + G1–G14)",
  "cov.hint": "For reviewers: jump from the source sheet's function No. to the screen. “External” items need tenant details, AWS credentials, SES approval or IPA's forms.",
  "cov.function": "Function",
  "cov.status": "Status",
  "cov.note": "Note",
  "cov.open": "Open",
  "cov.s.DONE": "Done",
  "cov.s.PARTIAL": "Partial",
  "cov.s.NOT_BUILT": "Not built",
  "cov.s.EXTERNAL": "External",
  "login.sso": "Sign in with {name}",
  "login.ssoOr": "or with a local account:",
  "login.oidc.NOT_PROVISIONED": "This IdP account is not registered yet. Ask an administrator to add you.",
  "login.oidc.EMAIL_UNVERIFIED": "Your e-mail address is not verified at the identity provider.",
  "login.oidc.TENANT_MISMATCH": "Please sign in with your company account (different domain / tenant).",
  "login.oidc.USER_NOT_ACTIVE": "This account is suspended or its user has left the company.",
  "login.oidc.IDENTITY_CONFLICT": "A different identity is already linked to this user. Contact an administrator.",
  "login.oidc.FAILED": "SSO sign-in failed. Please try again.",
  // ── Notifications ──
  "notif.title": "Notifications",
  "notif.empty": "No notifications.",
  "notif.readAll": "Mark all as read",
  "notif.bellLabel": "Notifications ({n} unread)",
  "notif.kind.other": "Notice",
  "notif.kind.OBLIGATION_REMINDER": "Deadline reminder",
  "notif.kind.OBLIGATION_UNASSIGNED": "Role unassigned",
  "notif.kind.TRAINING_REMINDER": "Training pending",
  "notif.kind.REQUEST_PENDING_APPROVAL": "Approval requested",
  "notif.kind.REQUEST_APPROVER_UNRESOLVED": "No approver assigned",
  "notif.kind.REQUEST_DECIDED": "Request decided",
  "notif.kind.REQUEST_APPLIED": "Request implemented",
  "notif.kind.INSPECTION_RESPONSE_REQUESTED": "Inspection response requested",
  "notif.kind.UNUSED_ID_CANDIDATES": "Unused IDs",
  "set.title": "My settings",
  "set.password": "Password",
  "set.currentPw": "Current password",
  "set.newPw": "New password",
  "set.newPwAgain": "New password (confirm)",
  "set.changePw": "Change password",
  "set.pwChanged": "Changed.",
  "set.pwWrongCurrent": "Current password is incorrect",
  "set.pwSame": "The new password must differ from the current one",
  "set.pwPolicy": "The password does not meet the policy",
  "set.pwMismatch": "Does not match the confirmation",
  "totp.gate.title": "Multi-factor authentication setup required",
  "totp.gate.body": "People signing in with a local account (email + password) cannot use other screens until multi-factor authentication (TOTP) is registered. Follow the steps below; the screen returns to normal automatically once you're done.",
  "set.totp": "Multi-factor authentication (TOTP)",
  "set.totpOn": "Enabled",
  "set.totpOff": "Not set up",
  "set.totpHint": "Once set up, signing in requires a 6-digit code from an authenticator app as well as your password. Install an authenticator app (such as Google Authenticator or Microsoft Authenticator) on your phone first.",
  "set.totpBegin": "Start setup",
  "set.totpS1": "In your authenticator app, tap \"+\", choose \"Scan a QR code\", and scan the code below.",
  "set.totpS2": "Type the 6-digit code shown in the app into the field below, then select \"Verify and enable\".",
  "set.totpNote": "Nothing is turned on until the code is verified, so stopping partway will not lock you out.",
  "set.totpKeepSecret": "Keep this QR code and key private. Do not share them or take a screenshot.",
  "set.totpInvalid": "The code does not match. Enter the new code shown in the app. If this keeps happening, set your phone's date and time to update automatically.",
  "set.totpPhoneChange": "Before you change phones, turn this off here. You can then set it up again on the new phone.",
  "set.totpCantScan": "Can't scan the QR code?",
  "set.totpManual": "In the app, choose \"Enter a setup key\" (some apps call it \"Enter code manually\") and enter the key below. If asked for the type, choose \"Time based\".",
  "set.totpQrAlt": "QR code to scan with your authenticator app",
  "set.totpConfirm": "Verify and enable",
  "set.totpEnabled": "Multi-factor authentication is now enabled.",
  "set.totpDisable": "Disable",
  "set.totpDisableReason": "Reason for disabling (this is recorded)",
  "set.totpDisabled": "Disabled.",
  "set.policyTitle": "Password policy in effect",
  "set.policyLen": "At least {n} characters.",
  "set.policyMixed": "Must include upper case, lower case and digits.",

  "form.addRow": "Add row",
  "form.deleteRow": "Remove",
  "form.minRows": "Enter at least one row",
  "form.unsupported": "Some fields cannot be edited on this screen: {fields}",
  "form.requiredLegend": "* Required",
  "form.missing": "Please fill in the required fields: {fields}",
};

/**
 * 画面ごとの追加の文言は src/i18n/*.ts に置く（各ファイルが ja と en を export する）。
 * ここで全部を合成する。同じキーが重なったら後から読んだもの（i18n/*.ts）が勝つ。
 */
const extra = import.meta.glob<{ ja: Dict; en: Dict }>("./i18n/*.ts", { eager: true });
for (const m of Object.values(extra)) {
  Object.assign(ja, m.ja);
  Object.assign(en, m.en);
}

const DICTS: Record<Lang, Dict> = { ja, en };

/**
 * 画面ごとの辞書（i18n/lazy/*.ts）を足す。その画面のモジュールが読み込まれたとき（描画の前）に呼ばれる。
 * 初回の読み込みに全画面の文言を載せないため（分割読み込みと揃える）。
 */
export function addDict(jaPart: Dict, enPart: Dict): void {
  Object.assign(ja, jaPart);
  Object.assign(en, enPart);
}
const STORAGE_KEY = "scs.lang";

function detect(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "ja" || saved === "en") return saved;
  } catch { /* プライベートウィンドウ等では読めない。既定に倒す */ }
  // 日本語以外のブラウザは英語にする（インドネシア語UIは作らない）
  return navigator.language?.startsWith("ja") ? "ja" : "en";
}

type Listener = (lang: Lang) => void;
let current: Lang = detect();
const listeners = new Set<Listener>();

export const i18n = {
  get lang(): Lang { return current; },
  set(lang: Lang): void {
    current = lang;
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* 保存できなくても動く */ }
    for (const fn of listeners) fn(lang);
  },
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/**
 * 訳を引く。`{name}` を params で置換する。
 * **キーが辞書に無ければキーをそのまま返す**（英語側の訳し忘れが画面で見える）。
 * 空文字を返すと「消えた」ようにしか見えず、抜けに気付けない。
 */
export function translate(
  lang: Lang, key: string, params?: Record<string, string | number>,
): string {
  const s = DICTS[lang][key] ?? DICTS.ja[key] ?? key;
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (_m, k: string) => String(params[k] ?? `{${k}}`));
}
